import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { geolocationKeys, GeolocationSettings } from "./useGeolocationSettingsQuery";
import { Geofence } from "./useGeofencesQuery";

export function useUpdateGeolocationSettings(companyId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patch: Partial<GeolocationSettings>) => {
      if (!companyId) throw new Error("No company ID available");

      const { data, error } = await supabase.rpc('set_company_settings', {
        p_geo_enabled: patch.geo_enabled ?? null,
        p_geo_strict_mode: patch.geo_strict_mode ?? null,
        p_geo_default_radius_meters: patch.geo_default_radius_meters ?? null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: geolocationKeys.settings(companyId) 
      });
    },
  });
}

export function useCreateGeofence(companyId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (geofence: Omit<Geofence, 'id' | 'company_id' | 'created_at' | 'updated_at'>) => {
      if (!companyId) throw new Error("No company ID available");

      const { data, error } = await supabase
        .from("company_geofences")
        .insert({
          ...geofence,
          company_id: companyId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidate all geofence filter variants (active, inactive, all)
      queryClient.invalidateQueries({ 
        queryKey: ['geolocation', 'geofences', companyId] 
      });
    },
  });
}

export function useUpdateGeofence(companyId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      patch 
    }: { 
      id: string; 
      patch: Partial<Omit<Geofence, 'id' | 'company_id' | 'created_at' | 'updated_at'>> 
    }) => {
      if (!companyId) throw new Error("No company ID available");

      const { data, error } = await supabase
        .from("company_geofences")
        .update(patch)
        .eq("id", id)
        .eq("company_id", companyId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidate all geofence filter variants (active, inactive, all)
      queryClient.invalidateQueries({ 
        queryKey: ['geolocation', 'geofences', companyId] 
      });
    },
  });
}

export function useDeleteGeofence(companyId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!companyId) throw new Error("No company ID available");

      // Soft delete: mark as inactive
      const { error } = await supabase
        .from("company_geofences")
        .update({ is_active: false })
        .eq("id", id)
        .eq("company_id", companyId);

      if (error) throw error;
    },
    onSuccess: () => {
      // Invalidate all geofence filter variants (active, inactive, all)
      queryClient.invalidateQueries({ 
        queryKey: ['geolocation', 'geofences', companyId] 
      });
    },
  });
}
